// RBAC Utility Functions for Frontend

export const ROLES = {
  ADMIN: 'Admin',
  APPROVER: 'Approver',
  LIBRARIAN: 'Librarian',
  TEACHER: 'Teacher',
  STUDENT: 'Student'
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

export interface Permission {
  resource: string;
  action: string;
}

/**
 * Normalize roles coming from backend (case-insensitive) and support legacy aliases.
 * - Some tenants may return "BGH" instead of "Approver"
 * - Defensive: trim + lowercase compare
 */
const normalizeRole = (role: string): string => {
  const v = (role || '').trim().toLowerCase();
  if (!v) return role;
  if (v === 'admin') return ROLES.ADMIN;
  if (v === 'approver' || v === 'bgh') return ROLES.APPROVER;
  if (v === 'librarian' || v === 'thu_thu' || v === 'thủ thư' || v === 'thu thu') return ROLES.LIBRARIAN;
  if (v === 'teacher' || v === 'giao_vien' || v === 'giáo viên' || v === 'giao vien') return ROLES.TEACHER;
  if (v === 'student' || v === 'hoc_sinh' || v === 'học sinh' || v === 'hoc sinh') return ROLES.STUDENT;
  // default: keep original
  return role;
};

const normalizeRoles = (userRoles: any): string[] => {
  // Defensive: backend may return roles as string, null, or array.
  let arr: string[] = [];

  if (Array.isArray(userRoles)) {
    arr = userRoles;
  } else if (typeof userRoles === 'string') {
    const raw = userRoles.trim();

    // Case 1: JSON-encoded array string: '["Admin","Approver"]'
    if (raw.startsWith('[') && raw.endsWith(']')) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          arr = parsed.map(String);
        } else {
          arr = [raw];
        }
      } catch {
        arr = [raw];
      }
    }
    // Case 2: Postgres array-like string: '{Admin,Approver}'
    else if (raw.startsWith('{') && raw.endsWith('}')) {
      const inner = raw.slice(1, -1);
      arr = inner ? inner.split(',').map((s) => s.replace(/^"+|"+$/g, '')) : [];
    } else {
      arr = [raw];
    }
  }

  // handle accidental comma-separated string entries: ["Admin,Approver"]
  return arr
    .flatMap((r) => (typeof r === 'string' ? r.split(',') : []))
    .map((r) => normalizeRole(r));
};

/**
 * Check if user has a specific role
 */
export const hasRole = (userRoles: string[], role: Role): boolean => {
  return normalizeRoles(userRoles).includes(role);
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (userRoles: string[], roles: Role[]): boolean => {
  const normalized = normalizeRoles(userRoles);
  return roles.some(role => normalized.includes(role));
};

/**
 * Check if user has all of the specified roles
 */
export const hasAllRoles = (userRoles: string[], roles: Role[]): boolean => {
  const normalized = normalizeRoles(userRoles);
  return roles.every(role => normalized.includes(role));
};

/**
 * Check if user is Admin
 */
export const isAdmin = (userRoles: string[]): boolean => {
  return hasRole(userRoles, ROLES.ADMIN);
};

/**
 * Check if user is Admin or Approver
 */
export const isAdminOrApprover = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER]);
};

/**
 * Check if user is Approver
 */
export const isApprover = (userRoles: string[]): boolean => {
  return hasRole(userRoles, ROLES.APPROVER);
};

/**
 * Check if user can manage users (Admin only)
 */
export const canManageUsers = (userRoles: string[]): boolean => {
  return hasRole(userRoles, ROLES.ADMIN);
};

/**
 * Check if user can manage books (Admin, Approver, or Librarian)
 */
export const canManageBooks = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.LIBRARIAN]);
};

/**
 * Check if user can manage resources (Admin, Approver, or Librarian)
 */
export const canManageResources = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.LIBRARIAN]);
};

/**
 * Check if user can manage categories (Admin, Approver, or Librarian)
 */
export const canManageCategories = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.LIBRARIAN]);
};

/**
 * Check if user can manage subjects (Admin or Approver only)
 */
export const canManageSubjects = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER]);
};

/**
 * Check if user can manage lesson plans (Admin, Approver, or Teacher)
 */
export const canManageLessonPlans = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.TEACHER]);
};

/**
 * Check if user can approve lesson plans (Admin or Approver)
 */
export const canApproveLessonPlans = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER]);
};

/**
 * Check if user can manage lesson plan templates (Admin, Approver, or Librarian)
 */
export const canManageLessonPlanTemplates = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.LIBRARIAN]);
};

/**
 * Check if user can view reports (Admin, Approver, or Librarian only - Teacher cannot view reports)
 */
export const canViewReports = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.LIBRARIAN]);
};

/**
 * Check if user can view all reports (Admin, Approver, or Librarian)
 */
export const canViewAllReports = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.LIBRARIAN]);
};

/**
 * Check if user is Teacher
 */
export const isTeacher = (userRoles: string[]): boolean => {
  return hasRole(userRoles, ROLES.TEACHER);
};

/**
 * Check if user is Student
 */
export const isStudent = (userRoles: string[]): boolean => {
  return hasRole(userRoles, ROLES.STUDENT);
};

/**
 * Check if user can upload resources (Admin, Approver, Librarian, or Teacher)
 */
export const canUploadResources = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.LIBRARIAN, ROLES.TEACHER]);
};

/**
 * Check if user can manage books (add/edit/import) - Admin, Approver, or Librarian only
 */
export const canManageBooksFull = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.LIBRARIAN]);
};

/**
 * Check if user can borrow/return books (All authenticated users)
 */
export const canBorrowBooks = (userRoles: string[]): boolean => {
  return userRoles.length > 0; // All authenticated users can borrow
};

/**
 * Check if user can checkout for others (Admin, Approver, or Librarian)
 */
export const canCheckoutForOthers = (userRoles: string[]): boolean => {
  return hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.APPROVER, ROLES.LIBRARIAN]);
};

/**
 * Check if user can manage backups (Admin or Approver only)
 */
export const canManageBackups = (userRoles: string[]): boolean => {
  return isAdminOrApprover(userRoles);
};

/**
 * Check if user can manage AI Knowledge (Admin or Approver only)
 */
export const canManageAIKnowledge = (userRoles: string[]): boolean => {
  return isAdminOrApprover(userRoles);
};

